/**
 * API Configuration and Endpoints
 *
 * This file centralizes all API endpoint definitions.
 * All endpoint paths are relative to the base URL configured in the HTTP client.
 */

// ============================================================================
// API Configuration
// ============================================================================

export const API_CONFIG = {
    /** Request timeout in milliseconds */
    TIMEOUT: 10_000,

    /** Allow more time for initial auth requests on cold-started backends */
    AUTH_TIMEOUT: 30_000,

    /** API version prefix */
    VERSION: "v1",

    /** Production API base URL */
    PRODUCTION_URL: "https://ridetofive-backend.onrender.com/api/v1",

    /** Development API path (proxied through Vite) */
    DEVELOPMENT_PATH: "/api/v1",
} as const;

// ============================================================================
// API Endpoints
// ============================================================================

/**
 * Authentication endpoints
 */
export const AUTH_ENDPOINTS = {
    /** Admin login endpoint */
    ADMIN_LOGIN: "/auth/login/admin",

    /** Authenticated profile */
    PROFILE: "/auth/profile",

    /** Request password reset code */
    FORGOT_PASSWORD: "/auth/forgot-password",

    /** Verify password reset code */
    VERIFY_RESET_CODE: "/auth/reset-password/verify-code",

    /** Confirm password reset with new password */
    CONFIRM_RESET_PASSWORD: "/auth/reset-password/confirm",

    /** Refresh access token */
    REFRESH_TOKEN: "/auth/refresh-token",

    /** Logout (if implemented) */
    LOGOUT: "/auth/logout",
} as const;

/**
 * Authentication routes that must remain public.
 * Requests to these endpoints should not inherit any stored bearer token.
 */
export const PUBLIC_AUTH_ENDPOINTS = [
    AUTH_ENDPOINTS.ADMIN_LOGIN,
    AUTH_ENDPOINTS.FORGOT_PASSWORD,
    AUTH_ENDPOINTS.VERIFY_RESET_CODE,
    AUTH_ENDPOINTS.CONFIRM_RESET_PASSWORD,
] as const;

/**
 * Admin user management endpoints
 */
export const ADMIN_ENDPOINTS = {
    /** User statistics */
    USER_STATS: "/admin/users/stats",

    /** Update user status (activate, deactivate, suspend) */
    USER_STATUS: (userId: string) => `/admin/users/${userId}/status`,
} as const;

export const USERS_INTELLIGENCE_ENDPOINTS = {
    BASE: "/admin/users/intelligence",
    SUMMARY: "/admin/users/intelligence/summary",
    DIRECTORY: "/admin/users/intelligence/directory",
    MAP: "/admin/users/intelligence/map",
} as const;

export const BUSINESS_SETTINGS_ENDPOINTS = {
    BASE: "/admin/settings/business",
    GET: "/admin/settings/business",
    UPDATE: "/admin/settings/business",
} as const;

/**
 * Dashboard Statistics endpoints
 * Accessible to both ADMIN and SUPER_ADMIN roles
 */
export const DASHBOARD_ENDPOINTS = {
    /** User statistics */
    USER_STATS: "/admin/users/stats",

    /** Ride offers and bookings statistics */
    RIDE_STATS: "/admin/rides/stats",

    /** Trip progression and rating statistics */
    TRIP_STATS: "/admin/trips/stats",

    /** Wallet and financial statistics */
    WALLET_STATS: "/admin/wallet/stats",
} as const;

/**
 * Super Admin - Admin Management endpoints
 * These endpoints require SUPER_ADMIN role
 */
export const ADMIN_MANAGEMENT_ENDPOINTS = {
    /** Create a new admin */
    CREATE: "/admin/create",

    /** List all admins (paginated) */
    LIST: "/admin/list",

    /** Get admin details by ID */
    DETAILS: (adminId: string) => `/admin/admins/${adminId}`,

    /** Get permission catalog */
    PERMISSIONS_CATALOG: "/admin/permissions/catalog",

    /** Update admin profile */
    UPDATE: (adminId: string) => `/admin/admins/${adminId}`,

    /** Update admin permissions */
    PERMISSIONS: (adminId: string) => `/admin/admins/${adminId}/permissions`,

    /** Update admin status */
    STATUS: (adminId: string) => `/admin/admins/${adminId}/status`,

    /** Reset admin password */
    RESET_PASSWORD: (adminId: string) => `/admin/admins/${adminId}/reset-password`,

    /** Delete admin */
    DELETE: (adminId: string) => `/admin/admins/${adminId}`,
} as const;

export const ADMIN_SETTINGS_ENDPOINTS = {
    /** Get currently authenticated admin profile */
    ME: "/admin/me",

    /** Change current admin password */
    CHANGE_PASSWORD: "/admin/change-password",
} as const;

/**
 * Drivers management endpoints
 */
export const DRIVERS_ENDPOINTS = {
    /** List all drivers (supports query params) */
    LIST: "/admin/drivers",

    /** Get single driver by ID */
    DETAILS: (driverId: string) => `/admin/drivers/${driverId}`,

    /** Delete driver by ID */
    DELETE: (driverId: string) => `/admin/drivers/${driverId}/delete`,

    /** Get driver rides (paginated, includes summary) */
    RIDES: (driverId: string) => `/admin/drivers/${driverId}/rides`,

    /** Get driver trips (paginated, includes summary) */
    TRIPS: (driverId: string) => `/admin/drivers/${driverId}/trips`,

    /** Get driver wallet (paginated payouts, includes summary) */
    WALLET: (driverId: string) => `/admin/drivers/${driverId}/wallet`,

    /** Review driver road worthiness document */
    ROAD_WORTHINESS_REVIEW: (driverId: string) =>
        `/admin/drivers/${driverId}/road-worthiness/review`,
} as const;

/**
 * Riders management endpoints
 */
export const RIDERS_ENDPOINTS = {
    /** List all riders (supports query params) */
    LIST: "/admin/riders",

    /** Get single rider by ID */
    DETAILS: (riderId: string) => `/admin/riders/${riderId}`,

    /** Delete rider by ID */
    DELETE: (riderId: string) => `/admin/riders/${riderId}`,

    /** Get rider bookings (paginated) */
    BOOKINGS: (riderId: string) => `/admin/riders/${riderId}/bookings`,

    /** Get rider trips (paginated, includes summary) */
    TRIPS: (riderId: string) => `/admin/riders/${riderId}/trips`,

    /** Get rider wallet (paginated transactions, includes summary) */
    WALLET: (riderId: string) => `/admin/riders/${riderId}/wallet`,
} as const;

/**
 * Ride Offers management endpoints
 */
export const RIDE_OFFERS_ENDPOINTS = {
    /** List all ride offers (supports query params) */
    LIST: "/admin/rides",

    /** Get single ride offer details by ID */
    DETAILS: (rideOfferId: string) => `/admin/rides/${rideOfferId}`,

    /** Cancel a ride offer */
    CANCEL: (rideOfferId: string) => `/admin/rides/${rideOfferId}/cancel`,

    /** Suspend or unsuspend a ride offer */
    SUSPEND: (rideOfferId: string) => `/admin/rides/${rideOfferId}/suspend`,
} as const;

/**
 * Bookings management endpoints
 */
export const BOOKINGS_ENDPOINTS = {
    /** List all bookings (supports query params) */
    LIST: "/admin/bookings",

    /** Get single booking details by ID */
    DETAILS: (bookingId: string) => `/admin/bookings/${bookingId}`,

    /** Cancel a booking */
    CANCEL: (bookingId: string) => `/admin/bookings/${bookingId}/cancel`,

    /** Refund a booking */
    REFUND: (bookingId: string) => `/admin/bookings/${bookingId}/refund`,
} as const;

/**
 * Trips management endpoints
 */
export const TRIPS_ENDPOINTS = {
    /** List all trips (supports query params) */
    LIST: "/admin/trips",

    /** Get single trip details by ID */
    DETAILS: (tripId: string) => `/admin/trips/${tripId}`,

    /** Cancel a trip */
    CANCEL: (tripId: string) => `/admin/trips/${tripId}/cancel`,

    /** Force complete a trip */
    COMPLETE: (tripId: string) => `/admin/trips/${tripId}/complete`,
} as const;

/**
 * Wallets management endpoints
 */
export const WALLETS_ENDPOINTS = {
    /** List all wallets (supports query params) */
    LIST: "/admin/wallets",

    /** Get wallet details by ID */
    DETAILS: (walletId: string) => `/admin/wallets/${walletId}`,

    /** Credit a wallet */
    CREDIT: (walletId: string) => `/admin/wallets/${walletId}/credit`,

    /** Debit a wallet */
    DEBIT: (walletId: string) => `/admin/wallets/${walletId}/debit`,

    /** Freeze/unfreeze a wallet */
    FREEZE: (walletId: string) => `/admin/wallets/${walletId}/freeze`,
} as const;

/**
 * Transactions management endpoints
 */
export const TRANSACTIONS_ENDPOINTS = {
    /** List all transactions (supports query params) */
    LIST: "/admin/transactions",

    /** Get transaction details by ID */
    DETAILS: (transactionId: string) => `/admin/transactions/${transactionId}`,
} as const;

/**
 * Payments management endpoints
 */
export const PAYMENTS_ENDPOINTS = {
    /** List all payments (supports query params) */
    LIST: "/admin/payments",

    /** Get payment details by ID */
    DETAILS: (paymentId: string) => `/admin/payments/${paymentId}`,
} as const;

/**
 * Platform wallet endpoints
 */
export const PLATFORM_WALLET_ENDPOINTS = {
    /** Get platform wallet balance and commission summary */
    GET: "/admin/platform-wallet",
} as const;

/**
 * Payouts management endpoints
 */
export const PAYOUTS_ENDPOINTS = {
    /** Retry a failed payout */
    RETRY: (payoutId: string) => `/admin/payouts/${payoutId}/retry`,

    /** Cancel a pending payout */
    CANCEL: (payoutId: string) => `/admin/payouts/${payoutId}/cancel`,
} as const;

/**
 * Fraud & Safety Monitoring endpoints
 */
export const FRAUD_ENDPOINTS = {
    /** List fraud alerts */
    ALERTS_LIST: "/admin/fraud/alerts",

    /** Get fraud alert details */
    ALERT_DETAILS: (alertId: string) => `/admin/fraud/alerts/${alertId}`,

    /** Update fraud alert status */
    ALERT_UPDATE_STATUS: (alertId: string) => `/admin/fraud/alerts/${alertId}/status`,

    /** Get fraud monitoring stats */
    STATS: "/admin/fraud/stats",

    /** List investigation cases */
    CASES_LIST: "/admin/fraud/cases",

    /** Get investigation case details */
    CASE_DETAILS: (caseIdOrNumber: string) => `/admin/fraud/cases/${caseIdOrNumber}`,

    /** Create investigation case */
    CASE_CREATE: "/admin/fraud/cases",

    /** Update investigation case */
    CASE_UPDATE: (caseId: string) => `/admin/fraud/cases/${caseId}`,

    /** Get user risk score */
    USER_RISK_SCORE: (userId: string) => `/admin/fraud/users/${userId}/risk-score`,

    /** Request user re-verification */
    REQUEST_REVERIFICATION: (userId: string) => `/admin/fraud/users/${userId}/request-reverification`,

    /** Run suspicious payments detection */
    DETECT_SUSPICIOUS_PAYMENTS: "/admin/fraud/detection/suspicious-payments",

    /** Run high cancellation detection */
    DETECT_HIGH_CANCELLATION: "/admin/fraud/detection/high-cancellation",

    /** Run fake bookings detection */
    DETECT_FAKE_BOOKINGS: "/admin/fraud/detection/fake-bookings",

    /** Run all fraud detection */
    DETECT_RUN_ALL: "/admin/fraud/detection/run-all",
} as const;

/**
 * Live Map Monitoring endpoints
 */
export const LIVE_ENDPOINTS = {
    /** Get active drivers with live locations */
    DRIVERS: "/admin/live/drivers",

    /** Track specific driver */
    DRIVER_DETAILS: (driverId: string) => `/admin/live/drivers/${driverId}`,

    /** Get active trips */
    TRIPS: "/admin/live/trips",

    /** Track specific trip */
    TRIP_DETAILS: (tripId: string) => `/admin/live/trips/${tripId}`,

    /** Get live platform stats */
    STATS: "/admin/live/stats",

    /** Get ride clusters */
    CLUSTERS: "/admin/live/clusters",

    /** Get demand heatmap */
    HEATMAP: "/admin/live/heatmap",

    /** Get supply-demand analysis */
    SUPPLY_DEMAND: "/admin/live/supply-demand",
} as const;

// ============================================================================
// Analytics & Reports Endpoints
// ============================================================================

export const MESSAGING_ENDPOINTS = {
    PUSH_USER: "/admin/notifications/send/user",
    ANNOUNCEMENTS: "/admin/messaging/announcements",
    ANNOUNCEMENTS_DRAFT: "/admin/messaging/announcements/draft",
    ANNOUNCEMENTS_SCHEDULE: "/admin/messaging/announcements/schedule",
    ANNOUNCEMENT_SEND: (id: string) => `/admin/messaging/announcements/${id}/send`,
    ANNOUNCEMENT_CANCEL: (id: string) => `/admin/messaging/announcements/${id}/cancel`,
    ANNOUNCEMENT_DETAILS: (id: string) => `/admin/messaging/announcements/${id}`,
    STATS: "/admin/messaging/stats",
} as const;

export const AUDIT_ENDPOINTS = {
    LOGS: "/admin/audit/logs",
    LOG_DETAILS: (id: string) => `/admin/audit/logs/${id}`,
    ADMIN_ACTIONS: (adminId: string) => `/admin/audit/admins/${adminId}/actions`,
    USER_ACTIONS: (userId: string) => `/admin/audit/users/${userId}/actions`,
    RESOURCE_TRAIL: (resourceType: string, resourceId: string) =>
        `/admin/audit/resources/${resourceType}/${resourceId}`,
    FAILED_ACTIONS: "/admin/audit/security/failed-actions",
    SENSITIVE_ACTIONS: "/admin/audit/security/sensitive-actions",
    STATISTICS: "/admin/audit/statistics",
    IP_ACTIVITY: (adminId: string) => `/admin/audit/admins/${adminId}/ip-activity`,
    CATEGORIES: "/admin/audit/categories",
    STATUSES: "/admin/audit/statuses",
    EXPORT: "/admin/audit/export",
    MY_ACTIONS: "/admin/audit/my-actions",
} as const;

/**
 * Driver License Verification endpoints
 */
export const DRIVER_LICENSE_ENDPOINTS = {
    /** Operational queue for driver license verification records */
    QUEUE: "/admin/approvals/driver-license-verifications",

    /** Full details of a single verification record */
    DETAILS: (verificationId: string) =>
        `/admin/approvals/driver-license-verifications/${verificationId}`,

    /** Manual review action endpoint */
    REVIEW: (verificationId: string) =>
        `/admin/approvals/driver-license-verifications/${verificationId}/review`,

    /** Convenience endpoint for immediate requeue */
    REQUEUE: (verificationId: string) =>
        `/admin/approvals/driver-license-verifications/${verificationId}/requeue`,
} as const;

export const APPROVAL_QUEUE_ENDPOINTS = {
    LIST: "/admin/approvals/queue",
    DETAILS: (approvalId: string) => `/admin/approvals/queue/${approvalId}`,
    APPROVE: (approvalId: string) => `/admin/approvals/queue/${approvalId}/approve`,
    REJECT: (approvalId: string) => `/admin/approvals/queue/${approvalId}/reject`,
    CANCEL: (approvalId: string) => `/admin/approvals/queue/${approvalId}/cancel`,
} as const;

export const ANALYTICS_ENDPOINTS = {
    REVENUE: "/admin/analytics/revenue",
    REVENUE_HOURLY: "/admin/analytics/revenue/hourly",
    TRIPS: "/admin/analytics/trips",
    CANCELLATIONS: "/admin/analytics/trips/cancellations",
    USERS: "/admin/analytics/users",
    DRIVERS: "/admin/analytics/users/drivers",
    GEOGRAPHIC: "/admin/analytics/geographic",
    REGIONS: "/admin/analytics/geographic/regions",
    OPERATIONAL: "/admin/analytics/operational",
    QUALITY: "/admin/analytics/quality",
    SUMMARY: "/admin/analytics/summary",
} as const;

// ============================================================================
// Endpoint Groups (for easy importing)
// ============================================================================

export const API_ENDPOINTS = {
    AUTH: AUTH_ENDPOINTS,
    ADMIN: ADMIN_ENDPOINTS,
    USERS_INTELLIGENCE: USERS_INTELLIGENCE_ENDPOINTS,
    BUSINESS_SETTINGS: BUSINESS_SETTINGS_ENDPOINTS,
    ADMIN_MANAGEMENT: ADMIN_MANAGEMENT_ENDPOINTS,
    DASHBOARD: DASHBOARD_ENDPOINTS,
    DRIVERS: DRIVERS_ENDPOINTS,
    RIDERS: RIDERS_ENDPOINTS,
    RIDE_OFFERS: RIDE_OFFERS_ENDPOINTS,
    BOOKINGS: BOOKINGS_ENDPOINTS,
    TRIPS: TRIPS_ENDPOINTS,
    WALLETS: WALLETS_ENDPOINTS,
    TRANSACTIONS: TRANSACTIONS_ENDPOINTS,
    PAYMENTS: PAYMENTS_ENDPOINTS,
    PLATFORM_WALLET: PLATFORM_WALLET_ENDPOINTS,
    PAYOUTS: PAYOUTS_ENDPOINTS,
    FRAUD: FRAUD_ENDPOINTS,
    LIVE: LIVE_ENDPOINTS,
    MESSAGING: MESSAGING_ENDPOINTS,
    AUDIT: AUDIT_ENDPOINTS,
    ANALYTICS: ANALYTICS_ENDPOINTS,
    DRIVER_LICENSE: DRIVER_LICENSE_ENDPOINTS,
    APPROVAL_QUEUE: APPROVAL_QUEUE_ENDPOINTS,
} as const;

// Type exports for endpoint values
export type AuthEndpoint = (typeof AUTH_ENDPOINTS)[keyof typeof AUTH_ENDPOINTS];
export type AdminEndpoint = (typeof ADMIN_ENDPOINTS)[keyof typeof ADMIN_ENDPOINTS];
